import { Link } from 'react-router-dom';
import { Sparkles, FileDown, TrendingUp, ArrowRight } from 'lucide-react';

/**
 * YieldAiBanner — invita il visitatore a provare Yield AI PRO.
 * Due varianti:
 *   - "hero"    (default): grande banner glassmorphism con CTA + stats
 *   - "compact": riga sottile adatta sopra elenchi (Partite, Esplora)
 */
export default function YieldAiBanner({ variant = 'hero', className = '' }) {
  if (variant === 'compact') {
    return (
      <Link
        to="/stima-quintali"
        data-testid="yield-banner-compact"
        className={
          'group relative flex items-center justify-between gap-4 rounded-2xl ' +
          'glass-vp px-5 py-4 border border-chartreuse/20 hover:border-chartreuse/50 transition ' +
          className
        }
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-chartreuse/15 grid place-items-center shrink-0">
            <Sparkles size={18} className="text-chartreuse" strokeWidth={1.6}/>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-display text-base md:text-lg tracking-tight truncate">Prova Yield AI PRO</span>
              <span className="text-[9px] uppercase tracking-widest font-bold text-chartreuse px-2 py-0.5 rounded-full border border-chartreuse/40">Nuovo · Gratis</span>
            </div>
            <div className="text-xs text-muted-foreground truncate">
              Stima quintali + valore in euro + report PDF in 30 secondi
            </div>
          </div>
        </div>
        <ArrowRight size={16} className="text-chartreuse shrink-0 group-hover:translate-x-1 transition-transform"/>
      </Link>
    );
  }

  return (
    <section
      data-testid="yield-banner-hero"
      className={
        'relative rounded-3xl glass-vp noise-overlay overflow-hidden ' +
        'p-8 md:p-14 border border-chartreuse/20 ' +
        className
      }
    >
      {/* Glow */}
      <div aria-hidden className="absolute -top-32 -right-24 w-[420px] h-[420px] rounded-full bg-chartreuse/15 blur-[110px] animate-pulse-glow"/>
      <div aria-hidden className="absolute -bottom-32 -left-20 w-[360px] h-[360px] rounded-full bg-terracotta/10 blur-[100px]"/>

      <div className="relative z-10 grid lg:grid-cols-5 gap-8 items-center">
        <div className="lg:col-span-3">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-bold text-chartreuse mb-4">
            <Sparkles size={14}/> Novità · Yield AI PRO
          </div>
          <h2 className="font-display text-3xl md:text-5xl tracking-tighter leading-[1.02]">
            Scopri quanti quintali vale <br/>
            <span className="italic text-chartreuse">davvero</span> il tuo terreno.
          </h2>
          <p className="mt-5 text-sm md:text-base text-muted-foreground max-w-xl leading-relaxed">
            Il primo tool italiano che combina <b className="text-foreground">6 strati di evidenza</b>
            — rese ISTAT, clima ECMWF, topografia DEM, agronomia, foto AI e prezzi ISMEA — per darti
            una stima con <b className="text-foreground">valore in euro</b>, benchmark regionale e <b className="text-foreground">report PDF</b> pronto per banca e compratori.
          </p>

          <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5"><TrendingUp size={12} className="text-chartreuse"/> Scenari what-if con €/anno</span>
            <span className="inline-flex items-center gap-1.5"><FileDown size={12} className="text-chartreuse"/> PDF 2 pagine brandizzato</span>
            <span className="inline-flex items-center gap-1.5"><Sparkles size={12} className="text-chartreuse"/> Claude Sonnet 4.6 multi-photo</span>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/stima-quintali"
              data-testid="yield-banner-cta"
              className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-chartreuse text-obsidian font-medium hover:opacity-90 transition shadow-[0_0_40px_-10px_rgba(180,255,0,0.6)]"
            >
              Prova gratis <ArrowRight size={16}/>
            </Link>
            <Link
              to="/stima-quintali"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-border text-sm font-medium hover:bg-secondary/50 transition"
            >
              Vedi un esempio di report
            </Link>
          </div>
          <div className="mt-3 text-[11px] text-muted-foreground">
            Nessuna registrazione · Dati non salvati · 100% gratuito in Beta
          </div>
        </div>

        {/* Mock card report */}
        <div className="lg:col-span-2 hidden md:block">
          <div className="relative rounded-2xl glass-vp p-5 md:p-6 border border-white/10 transform rotate-[-1.2deg] hover:rotate-0 transition-transform duration-700">
            <div className="text-[10px] uppercase tracking-widest text-chartreuse font-bold">Yield AI PRO · Report</div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div>
                <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Produzione</div>
                <div className="font-display text-2xl md:text-3xl text-chartreuse leading-tight">200 q</div>
                <div className="text-[10px] text-muted-foreground">range 75–385</div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-widest text-muted-foreground">Valore</div>
                <div className="font-display text-2xl md:text-3xl text-terracotta leading-tight">€ 32k</div>
                <div className="text-[10px] text-muted-foreground">range 8–85</div>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-1.5">
              <span className="text-[9px] px-2 py-0.5 rounded-full bg-chartreuse/15 text-chartreuse border border-chartreuse/30 uppercase tracking-widest font-bold">Confidenza 72%</span>
              <span className="text-[9px] px-2 py-0.5 rounded-full bg-chartreuse/10 text-chartreuse border border-chartreuse/20 uppercase tracking-widest font-bold">Sopra media</span>
            </div>
            <div className="mt-4 space-y-1.5 text-[11px]">
              <div className="flex justify-between items-center py-1.5 border-b border-white/5">
                <span className="text-muted-foreground">Clima</span>
                <span className="font-bold text-chartreuse">1.00×</span>
              </div>
              <div className="flex justify-between items-center py-1.5 border-b border-white/5">
                <span className="text-muted-foreground">Topografia</span>
                <span className="font-bold text-chartreuse">1.00×</span>
              </div>
              <div className="flex justify-between items-center py-1.5">
                <span className="text-muted-foreground">Agronomia</span>
                <span className="font-bold text-chartreuse">1.00×</span>
              </div>
            </div>
            <div className="mt-4 rounded-lg bg-chartreuse/5 border border-chartreuse/20 p-2.5 text-[10px]">
              <div className="flex justify-between items-center">
                <span className="text-foreground">Irrigazione a goccia</span>
                <span className="font-bold text-terracotta">+€ 8.960/anno</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
